import { CloseOutlined } from '@vindr/icons';
import { useGetAccounts } from '@vindr/services/actions/account.action';
import { useGetFullEsignHistoryByOrder } from '@vindr/services/actions/esign.action';
import { useGetOrders } from '@vindr/services/actions/order.action';
import {
  useGetLatestReportByOrder,
  useGetReportByVersionId,
  useGetReportVersions,
} from '@vindr/services/actions/report.action';
import { getImageUrl, setImageSrcToStructure } from '@vindr/services/apis/element.api';
import { ESIGN_STATUS } from '@vindr/services/apis/esign.api';
import { Section } from '@vindr/services/apis/templateStructuredReport';
import { SORT_ORDER, USER_STATUS } from '@vindr/services/constants/filter.constant';
import { queryKey } from '@vindr/services/constants/queryClientKey';
import { Empty, TextEditor } from '@vindr/ui';
import { createStructuredElement } from '@vindr/ui/src/components/common/StructuredReport/StructuredReportForm';
import { getArrayVal, sortObjectKeys, uuidv4 } from '@vindr/utils/helpers/common.helper';
import { Empty as AntEmpty, Button } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import {
  Dispatch,
  FC,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';
import { ReportFormStates } from '../ReportForm';
import History from './History';
import SelectOrderHeader from './SelectOrderHeader';
import HtmldiffWorker from './htmldiff.worker';
import useContentConclusion from './useContentConclusion';
import { ParamsGetOrders } from '@vindr/services/apis/order.api';

const UUID_editor = uuidv4();
const compareKey = 'compare';

const CompareEditor: FC<{
  canSeeReport: ReportFormStates['canSeeReport'];
  className?: string;
  isCompare: boolean;
  matchedOrders: ReportFormStates['matchedOrders'];
  message: MessageInstance;
  onClose: () => void;
  order: ReportFormStates['order'];
  selectedOrder: ReportFormStates['selectedOrder'];
  selectedReport: ReportFormStates['selectedReport'];
  setSelectedOrder: Dispatch<SetStateAction<ReportFormStates['selectedOrder']>>;
}> = ({
  canSeeReport,
  className,
  isCompare,
  matchedOrders,
  message,
  onClose,
  order,
  selectedOrder,
  selectedReport,
  setSelectedOrder,
}) => {
  const { t } = useTranslation('Vindoc');
  const { EMPTY_LINE, LOCK_EMPTY_LINE, CONTENT_TITLE, CONCLUSION_TITLE } = useContentConclusion();

  const findOrder = useMemo(
    () =>
      (order && matchedOrders && matchedOrders.find((it) => it.id === order.id)) ||
      matchedOrders?.[0],
    [matchedOrders, order]
  );

  const orderPrams: ParamsGetOrders = useMemo(
    () => ({
      patient_id: findOrder?.visited_patient?.patient_id,
      limit: 100,
      offset: 0,
      sort_field: 'patient_arrived_time',
      sort_order: SORT_ORDER.DESC,
    }),
    [findOrder?.visited_patient?.patient_id]
  );

  const { data: orderListResponse } = useGetOrders(orderPrams, {
    enabled: !!findOrder?.visited_patient?.patient_id,
    queryKey: [queryKey.orders, compareKey, sortObjectKeys(orderPrams)],
  });
  const orderList = useMemo(() => orderListResponse?.data, [orderListResponse?.data]);

  const {
    data: reportResponse,
    isInitialLoading: isLoadingReport,
    isRefetching: isRefetchingReport,
    refetch,
  } = useGetLatestReportByOrder(selectedOrder?.id, {
    enabled: !!selectedOrder?.id,
    queryKey: [queryKey.latest_report_by_order, compareKey, selectedOrder?.id],
    onError(err) {
      message.error(err?.response?.data?.error_message);
    },
  });
  const report = useMemo(() => reportResponse?.data, [reportResponse?.data]);
  const { data: reportVersions, refetch: refetchReportVersions } = useGetReportVersions(
    report?.id,
    { enabled: !!report?.id, queryKey: [queryKey.report_versions, compareKey, report?.id] }
  );
  const [selectedReportVersion, setSelectedReportVersion] = useState(report?.report_version?.id);
  const { data: selectedReportResponse } = useGetReportByVersionId(selectedReportVersion, {
    enabled: !!selectedReportVersion,
    queryKey: [queryKey.report_by_version_id, compareKey, selectedReportVersion],
  });
  const [structureReportCompareSections, setStructureReportCompareSections] =
    useImmer<Section[]>(null);
  const selectedCompareReport = useMemo(() => {
    const result = getArrayVal(selectedReportResponse?.data);
    setStructureReportCompareSections(() => result?.structure?.data || []);
    return result;
  }, [selectedReportResponse?.data, setStructureReportCompareSections]);

  const esignParams = useMemo(
    () => ({ limit: 1, offset: 0, order_id: selectedOrder?.id }),
    [selectedOrder?.id]
  );

  const { data: esignResponse } = useGetFullEsignHistoryByOrder(esignParams, {
    enabled: !!selectedOrder?.id,
    queryKey: [queryKey.full_esign_history_by_order, compareKey, sortObjectKeys(esignParams)],
    refetchInterval: (response) =>
      getArrayVal(response?.data)?.status === ESIGN_STATUS['ESIGN.Waiting'] ? 1000 : 0,
  });

  const mergeContentConclusion = useCallback(
    (content: string, conclusion: string) => {
      return `${CONTENT_TITLE}${content ?? ''}${EMPTY_LINE}${LOCK_EMPTY_LINE}${CONCLUSION_TITLE}${
        conclusion ?? ''
      }${EMPTY_LINE}`;
    },
    [t]
  );

  const is_structure_report = useMemo(
    () => selectedReport?.is_structure_report,
    [selectedReport?.is_structure_report]
  );

  const is_structure_compare_report = useMemo(
    () => selectedCompareReport?.is_structure_report,
    [selectedCompareReport?.is_structure_report]
  );

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [structureReportData, setStructureReportData] = useState('');
  const [structureReportCompareData, setStructureReportCompareData] = useState('');

  useEffect(() => {
    if (!is_structure_report) return;

    (async () => {
      const structureReportSections = selectedReport?.structure?.data;
      const addImageToSections = await setImageSrcToStructure(
        structureReportSections,
        getImageUrl
      ).catch((error) => (console.error(error), structureReportSections));
      const renderStructureData = createStructuredElement(addImageToSections)
        .map((x) => x?.render?.())
        .join('');

      setStructureReportData(renderStructureData);
    })();
  }, [is_structure_report, selectedReport?.structure?.data]);

  useEffect(() => {
    if (!is_structure_compare_report) return;

    (async () => {
      const addImageToSections = await setImageSrcToStructure(
        structureReportCompareSections,
        getImageUrl
      ).catch((error) => (console.error(error), structureReportCompareSections));
      const renderStructureData = createStructuredElement(addImageToSections)
        .map((x) => x?.render?.())
        .join('');

      setStructureReportCompareData(renderStructureData);
    })();
  }, [is_structure_compare_report, structureReportCompareSections]);

  const handleDiff = useCallback(() => {
    const worker = new HtmldiffWorker();

    setLoading(true);
    setResult('');
    setError(null);

    // Create the worker instance

    const contentAndConclusion = is_structure_report
      ? structureReportData
      : mergeContentConclusion(selectedReport?.content, selectedReport?.conclusion);
    const contentAndConclusionCompare = is_structure_compare_report
      ? structureReportCompareData
      : mergeContentConclusion(selectedCompareReport?.content, selectedCompareReport?.conclusion);

    // Post the data to the worker
    worker.postMessage({ oldHtml: contentAndConclusion, newHtml: contentAndConclusionCompare });

    // Listen for messages from the worker
    worker.onmessage = (event) => {
      const { success, diff, error } = event.data;

      if (success) {
        setResult(diff);
      } else {
        setError(error);
      }

      setLoading(false);
      worker.terminate(); // Terminate the worker after use
    };

    // Handle errors in the worker
    worker.onerror = (err) => {
      setError('An error occurred in the worker.');
      setLoading(false);
      worker.terminate(); // Terminate the worker on error
    };

    return () => {
      worker.terminate();
    };
  }, [
    is_structure_compare_report,
    is_structure_report,
    mergeContentConclusion,
    selectedCompareReport?.conclusion,
    selectedCompareReport?.content,
    selectedReport?.conclusion,
    selectedReport?.content,
    structureReportCompareData,
    structureReportData,
  ]);

  useEffect(() => {
    handleDiff();
  }, [handleDiff]);

  const esignStatus = useMemo(
    () => getArrayVal(esignResponse?.data)?.status,
    [esignResponse?.data]
  );

  useEffect(() => {
    if (!orderList?.length || selectedOrder) return;

    setSelectedOrder(orderList?.[0]);
  }, [orderList, selectedOrder, setSelectedOrder]);

  useEffect(() => {
    setSelectedReportVersion(report?.report_version?.id);
  }, [report?.report_version?.id]);

  const sessionUserParams = useMemo(
    () => ({ offset: 0, limit: 100, statuses: [USER_STATUS.ACTIVE], ids: [report?.session] }),
    [report?.session]
  );

  const sessionUser = useGetAccounts(sessionUserParams, {
    enabled: !!report?.session,
    queryKey: [queryKey.account, compareKey, sortObjectKeys(sessionUserParams)],
  })?.data?.data?.[0];

  const Action = () => (
    <div className="flex items-center gap-1">
      <History
        appearance="icon"
        className="m-0"
        refetch={refetchReportVersions}
        reportVersions={reportVersions?.data}
        selectedReportVersion={selectedReportVersion}
        setSelectedReportVersion={setSelectedReportVersion}
      />
      <Button className="pb-[25px]" size="small" type="text">
        <CloseOutlined onClick={onClose} />
      </Button>
    </div>
  );
  Action.displayName = 'Action';

  return (
    <section className={className}>
      <SelectOrderHeader
        disabled
        action={<Action />}
        disabledAction={false}
        esignStatus={esignStatus}
        isCompare={isCompare}
        matchedOrders={orderList}
        reportVersions={reportVersions?.data}
        selectedOrder={selectedOrder}
        selectedReportVersion={selectedReportVersion}
        onSelect={setSelectedOrder}
      />
      {!selectedCompareReport ? (
        <div className="flex min-h-80 flex-col items-center justify-center rounded-sm border border-secondary-main">
          <Empty
            className="m-0 text-lg"
            description={t('No report available')}
            image={AntEmpty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : canSeeReport(selectedCompareReport) ? (
        <div className="flex-1">
          <TextEditor
            disabled={true}
            height={'calc(100dvh - 355px)'}
            id={UUID_editor} //? For multiple text editor in one site
            loading={loading}
            menubar={false}
            value={result}
            initProps={{
              forced_root_block: 'div',
              menubar: false,
              plugins: [],
              readonly: true,
              statusbar: false,
              toolbar: false,
              images_upload_handler: null,
            }}
          />
        </div>
      ) : (
        <div className="flex min-h-80 flex-col items-center justify-center rounded-sm border border-secondary-main">
          <Empty
            image={AntEmpty.PRESENTED_IMAGE_SIMPLE}
            description={
              isEmpty(sessionUser)
                ? t('This report is being modified by another user')
                : `${t('This report is being modified by')} ${sessionUser?.username}${
                    sessionUser?.fullname ? ` : ${sessionUser?.fullname}` : ''
                  }`
            }
          />
        </div>
      )}
    </section>
  );
};

export default memo(
  CompareEditor,
  (prev, next) =>
    isEqual(prev.order, next.order) &&
    isEqual(prev.matchedOrders, next.matchedOrders) &&
    isEqual(prev.selectedReport, next.selectedReport) &&
    isEqual(prev.selectedOrder, next.selectedOrder) &&
    prev.isCompare === next.isCompare
);
